import { themeQuartz } from 'ag-grid-community';
import { useTheme } from '@mui/material/styles';
import { useEffect, useMemo } from 'react';
import { getResponsiveFontSize, getResponsiveHeaderHeight } from '../utils/agGridHelpers';

export interface UseAgGridThemeOptions {
  /** When set, styles are scoped to this class (e.g. grid className). When omitted, styles are global. */
  scopeId?: string;
  /** Row height in pixels. */
  rowHeight: number;
  /** Extra theme params merged into themeQuartz.withParams(). */
  themeParams?: Record<string, any>;
  /** When true, use higher z-index for popups (e.g. grid inside modal). */
  isInModal?: boolean;
  /** When true, rows show pointer cursor (e.g. when onCellClicked is provided). */
  clickable?: boolean;
}

function buildAgGridStyles(
  palette: Record<string, any>,
  zIndex: Record<string, number>,
  scopeId: string | undefined,
  isInModal: boolean,
  clickable: boolean,
): string {
  const pre = scopeId ? `.${scopeId} ` : '';
  const z = isInModal ? zIndex.agGridPopupModal : zIndex.agGridPopup;
  const zMini = isInModal ? zIndex.agGridPopupModal + 1 : zIndex.agGridPopup + 1;

  return `
    /* header input styling */
    ${pre}.ag-input-field-input {
      min-height: 28px;
    }

    ${pre}.ag-floating-row-filter {
      display: flex;
      gap: 10px;
    }

    /* Remove outer border of AG Grid table */
    ${pre}.ag-root-wrapper {
      border: none !important;
      background: ${palette.common?.white ?? '#fff'};
    }

    /* Remove horizontal scrollbar of fixed columns */
    .ag-body-horizontal-scroll .ag-horizontal-left-spacer,
    .ag-body-horizontal-scroll .ag-horizontal-right-spacer {
      visibility: hidden;
    }

    ${pre}.ag-theme-quartz .ag-header-row.ag-header-row-filter {
      padding-bottom: 0px;
    }

    /* First row border */
    ${pre}.ag-theme-quartz .ag-row:first-child,
    ${pre}.ag-row:first-child {
      border-top: 0.3px solid ${palette.inputField?.border ?? '#e0e0e0'} !important;
    }

    /* Hide default pagination */
    ${pre}.ag-paging-panel {
      display: none !important;
    }

    /* Loading overlay */
    .ag-overlay-loading-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    ${pre}.ag-input-field-input:disabled {
      background-color: ${palette.common?.white ?? '#fff'} !important;
      color: ${palette.secondary?.light ?? '#666'} !important;
      opacity: 1 !important;
    }

    /* Filter input placeholder styling */
    ${pre}.ag-input-field-input::placeholder,
    ${pre}.ag-floating-filter-input::placeholder {
      color: ${palette.buttonDisabledBg ?? '#9e9e9e'} !important;
      font-style: italic !important;
      opacity: 1 !important;
    }

    ${pre}.ag-input-field-input::-webkit-input-placeholder,
    ${pre}.ag-floating-filter-input::-webkit-input-placeholder {
      color: ${palette.buttonDisabledBg ?? '#9e9e9e'} !important;
      font-style: italic !important;
      opacity: 1 !important;
    }

    ${pre}.ag-input-field-input::-moz-placeholder,
    ${pre}.ag-floating-filter-input::-moz-placeholder {
      color: ${palette.buttonDisabledBg ?? '#9e9e9e'} !important;
      font-style: italic !important;
      opacity: 1 !important;
    }

    ${pre}.ag-input-field-input:-ms-input-placeholder,
    ${pre}.ag-floating-filter-input:-ms-input-placeholder {
      color: ${palette.buttonDisabledBg ?? '#9e9e9e'} !important;
      font-style: italic !important;
      opacity: 1 !important;
    }

    ${pre}.ag-floating-filter-button {
      opacity: 0 !important;
      pointer-events: none !important;
      width: 0 !important;
      margin: 0 !important;
      position: absolute !important;
      left: 15px !important;
    }

    ${pre}.ag-icon-menu-alt {
      display: none !important;
    }

    ${scopeId && clickable ? `${pre}.ag-row { cursor: pointer !important; }` : ''}

    /* Tooltip positioning */
    ${scopeId ? `${pre}.ag-tooltip-custom { position: absolute !important; z-index: ${zIndex.agGridTooltip} !important; }` : ''}
    ${scopeId ? `${pre}.ag-popup[class*="tooltip"] { pointer-events: none !important; z-index: ${zIndex.agGridTooltip} !important; }` : ''}

    /* Fix for filter popups appearing behind modals */
    ${scopeId ? `${pre}.ag-popup { z-index: ${z} !important; }` : `.ag-popup { z-index: ${z} !important; }`}

    .ag-menu {
      z-index: ${z} !important;
    }

    .ag-filter-popup {
      z-index: ${z} !important;
    }

    .ag-popup .ag-set-filter {
      z-index: ${z} !important;
    }

    .ag-popup .ag-filter-wrapper {
      z-index: ${z} !important;
    }

    .ag-popup .ag-mini-filter {
      opacity: 1 !important;
      pointer-events: auto !important;
      z-index: ${zMini} !important;
    }

    .ag-popup .ag-mini-filter .ag-input-field-input {
      opacity: 1 !important;
      pointer-events: auto !important;
      background-color: ${palette.common?.white ?? '#fff'} !important;
    }

    .ag-popup .ag-set-filter-list {
      pointer-events: auto !important;
    }
  `;
}

/**
 * Shared hook for AG Grid theme and injected styles.
 * Used by ClientSideAgGrid and ServerSideAgGrid for consistent styling.
 */
export function useAgGridTheme({
  scopeId,
  rowHeight,
  themeParams = {},
  isInModal = false,
  clickable = false,
}: UseAgGridThemeOptions) {
  const muiTheme = useTheme();
  const palette = muiTheme.palette as Record<string, any>;

  useEffect(() => {
    const styleId = scopeId ? `ag-grid-custom-styles-${scopeId}` : 'ag-grid-custom-styles';
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    styleElement.textContent = buildAgGridStyles(
      palette,
      muiTheme.zIndex as unknown as Record<string, number>,
      scopeId,
      isInModal,
      clickable,
    );

    return () => {
      const element = document.getElementById(styleId);
      if (element) element.remove();
    };
  }, [scopeId, isInModal, clickable, palette, muiTheme.zIndex]);

  return useMemo(
    () =>
      themeQuartz.withParams({
        backgroundColor: muiTheme.palette.common.white,
        headerBackgroundColor: muiTheme.palette.common.white,
        fontSize: getResponsiveFontSize(),
        rowHeight,
        headerHeight: getResponsiveHeaderHeight(),
        headerRowBorder: false,
        rowBorder: true,
        ...themeParams,
      }),
    [themeParams, rowHeight, muiTheme],
  );
}
